/** Small form pieces for the editor panels. */
import { useEffect, useState } from 'react';
import type { AnimalAreaKind, SpeciesWithOverrides } from '../../../shared/types';
import { BIOMES } from '../../render/palette';

/**
 * A number box that applies valid values while typing (within min–max) and tidies up on leaving the box.
 */
export function NumberField(props: {
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (v: number) => void;
  width?: string;
  label?: string;
}) {
  const { value, min, max, step = 1, onChange, width = '6em', label } = props;
  const [text, setText] = useState(String(value));
  useEffect(() => { setText((t) => (Number(t) === value ? t : String(value))); }, [value]);
  const clampTo = (v: number) => Math.min(max, Math.max(min, v));
  return (
    <input
      type="number"
      aria-label={label}
      min={min}
      max={max}
      step={step}
      value={text}
      style={{ width }}
      onChange={(e) => {
        setText(e.target.value);
        const v = Number(e.target.value);
        if (e.target.value !== '' && Number.isFinite(v) && v >= min && v <= max) onChange(v);
      }}
      onBlur={() => {
        const v = Number(text);
        const next = Number.isFinite(v) && text !== '' ? clampTo(v) : value;
        setText(String(next));
        if (next !== value) onChange(next);
      }}
      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
    />
  );
}

const KIND_GROUP: Record<AnimalAreaKind, string> = { habitat: 'Habitat animals', aviary: 'Aviary birds', aquarium: 'Aquarium animals' };

/** A drop-down of every animal, grouped by habitat / aviary / aquarium and sorted by name. */
export function SpeciesSelect(props: {
  species: SpeciesWithOverrides[];
  value: string | null;
  onChange: (id: string) => void;
  placeholder?: string;
  label?: string;
}) {
  const { species, value, onChange, placeholder, label } = props;
  const groups = (['habitat', 'aviary', 'aquarium'] as AnimalAreaKind[]).map((k) => ({
    k,
    list: species.filter((s) => s.kind === k).sort((a, b) => a.name.localeCompare(b.name)),
  }));
  const known = value === null || species.some((s) => s.id === value);
  return (
    <select aria-label={label} value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
      {(value === null || value === '') && <option value="" disabled>{placeholder ?? 'Pick an animal…'}</option>}
      {!known && value && <option value={value}>Unknown animal</option>}
      {groups.map((g) => g.list.length ? (
        <optgroup key={g.k} label={KIND_GROUP[g.k]}>
          {g.list.map((s) => <option key={s.id} value={s.id}>{s.name} ({BIOMES[s.biome].label.toLowerCase()})</option>)}
        </optgroup>
      ) : null)}
    </select>
  );
}
